import { batch, bind, h, signal, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { NWCloseButton } from "@/wc/components/atoms/nw-button/closeButton.tsx";
import type { PortfolioItemProject } from "@/wc/pages/portfolio/store/models.ts";
import { COMMUNITY_LINK_WITH_ERROR_UTM } from "@/globals/links.ts";
import { Timeout } from "@/types/timers.ts";
import { addToClipboard } from "@/utils/clipboard.ts";
import { PureComponent } from "@/utils/pureComponent.tsx";
import { networkSignal } from "@/utils/signals";
import { isValidURL } from "@/utils/url.ts";
import { DocumentationTheme, DocumentRef } from "@/api";
import { closeModal, NWButton, NWDropdown, NWIcon, NWProBadge, openProModal } from "@/wc/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";
import { initApi } from "@/utils";

import { DocumentURL } from "../../ui/documentURL";
import { SocialTooltip } from "../../ui/socialTooltip";
import {
  createSaveProjectOptions,
  getDisplayText,
  getIcon,
  isProOption,
  isProTheme,
  ShareAction,
  ShareOption,
} from "../../models";
import { ThemeSelector } from "./sections/themeSelector.tsx";
import {
  createDocumentationLinks,
  createPortfolioDocumentationLink,
  generatePrefilledPostUrl,
  triggerDownload,
} from "./utils.ts";

type Props = {
  close: () => void;
  onBack: () => void;
  retrieveFiles: () => Promise<void>;
  showActionNotification: (
    type: "success" | "error",
    title: string,
    timeMs?: number,
    link?: string | (() => void),
  ) => void;
  data: {
    projectId: string;
    files?: DocumentRef[];
  };
};

@tag("nw-project-features-share-options")
export class NWProjectShareDialogOptions extends PureComponent<Props> {
  static css = [tailwind];

  private selectedSaveProjectOption = signal<ShareAction>(ShareAction.SAVE_TO_PORTFOLIO);
  private selectedTheme = signal<DocumentationTheme>(DocumentationTheme.LIGHT);
  private documentUrl = signal<string | undefined>(undefined);
  private documentUrlError = signal<string | undefined>(undefined);

  private isDownloading = signal(false);
  private saveAsPDFNetwork = networkSignal();

  private timeoutId: Timeout | undefined;

  installed() {
    if (Store.user.preferences.documentationTheme?.value) {
      this.selectedTheme.value =
        isProTheme(Store.user.preferences.documentationTheme.value) && !Store.user.isPro()
          ? DocumentationTheme.LIGHT
          : Store.user.preferences.documentationTheme.value;
    }

    if (Store.user.preferences.profileURL?.value) {
      this.documentUrl.value = Store.user.preferences.profileURL?.value;
    }
  }

  uninstall(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  @bind
  private async handleOnSave() {
    const action = this.selectedSaveProjectOption.value;
    this.isDownloading.value = true;

    try {
      if (!this.props.data.files || this.props.data.files.length === 0) {
        await this.props.retrieveFiles();

        if (!this.props.data.files || this.props.data.files.length === 0) {
          this.props.showActionNotification("error", "Unable to retrieve your documentation. Please try again.");
          this.isDownloading.value = false;
          return;
        }
      }

      switch (action) {
        case ShareAction.COPY_URL: {
          await this.handleCopyURL();
          break;
        }
        case ShareAction.SAVE_AS_PDF: {
          await this.handleSaveToPDF();
          break;
        }
        case ShareAction.SAVE_TO_PORTFOLIO: {
          this.handleSaveToPortfolio();
          break;
        }
        case ShareAction.SAVE_AS_README:
        case ShareAction.SAVE_AS_MARKDOWN: {
          if (!Store.user.isPro()) {
            this.isDownloading.value = false;
            return;
          }
          const docRef = this.props.data.files?.find(d => d.type === "md");
          if (!docRef) {
            this.props.showActionNotification("error", "Download failed, please refresh and try again.");
            this.isDownloading.value = false;
            return;
          }

          const fileName =
            action === ShareAction.SAVE_AS_README ? `README.md` : `legendary-${this.props.data.projectId}.md`;
          triggerDownload(fileName, docRef);

          this.timeoutId = setTimeout(() => {
            this.isDownloading.value = false;
            this.props.showActionNotification("success", "Your download has started");
          }, 2000);
          break;
        }
        default:
          this.isDownloading.value = false;
          break;
      }
    } catch (error) {
      this.isDownloading.value = false;
      this.props.showActionNotification("error", "Something went wrong, please try again.");
    }
  }

  private async handleReprocess() {
    const projectId = Store.project.id.value;
    const projectMetadata = Store.project.metadata.value;
    const theme = this.selectedTheme.value;

    if (!projectId || !projectMetadata || !theme) {
      this.props.showActionNotification(
        "error",
        "Something went wrong, please refresh and try again.",
        undefined,
        COMMUNITY_LINK_WITH_ERROR_UTM,
      );
      return false;
    }

    if (!isEmpty(this.documentUrl.value) && !isValidURL(this.documentUrl.value!)) {
      batch(() => {
        this.documentUrlError.value = "The URL provided is not valid.";
        this.isDownloading.value = false;
      });

      return false;
    } else {
      this.documentUrlError.value = undefined;
    }

    try {
      const api = initApi();
      if (
        !Store.user.isPro() &&
        ((!isEmpty(Store.user.preferences.documentationTheme!.value) &&
          !isProTheme(Store.user.preferences.documentationTheme!.value!)) ||
          isEmpty(Store.user.preferences.documentationTheme!.value))
      ) {
        return true;
      }

      const currentPrefs = Store.user.preferences.getPreferencesDTO();

      const preferenceUpdate = await api.setUserPreferences({
        preferences: { ...currentPrefs, documentationTheme: theme, profileURL: this.documentUrl.value },
      });

      if (preferenceUpdate == null) {
        return false;
      }

      Store.user.preferences.documentationTheme!.value = theme;
      Store.user.preferences.profileURL!.value = this.documentUrl.value;

      // Re-trigger mission complete to trigger doc regen
      await api.regenerateProjectDocument({ projectId });
      await this.props.retrieveFiles();

      return true;
    } catch (error) {
      this.props.showActionNotification("error", "Something went wrong, please try again.");
      return false;
    }
  }

  @bind
  private handleSaveToPDF() {
    const saveReq = async () => {
      try {
        const shouldContinue = await this.handleReprocess();

        if (!shouldContinue) {
          this.isDownloading.value = false;
          return;
        }

        const docRef = this.props.data.files?.find(d => d.type === "pdf" && d._public === false);

        if (!docRef) {
          this.props.showActionNotification(
            "error",
            "Download failed, please refresh and try again.",
            undefined,
            COMMUNITY_LINK_WITH_ERROR_UTM,
          );
        }

        const fileName = `legendary-${this.props.data.projectId}.pdf`;
        triggerDownload(fileName, docRef!);
        this.trackDocumentationViewed();

        this.timeoutId = setTimeout(() => {
          this.isDownloading.value = false;
          this.props.showActionNotification("success", "Your PDF download has started");
        }, 2000);
      } catch (error) {
        this.props.showActionNotification("error", "Download failed, please refresh and try again.");
      } finally {
        this.isDownloading.value = false;
      }
    };

    this.saveAsPDFNetwork.run(saveReq);
  }

  @bind
  private async handleSaveToPortfolio() {
    try {
      const shouldContinue = await this.handleReprocess();
      if (!shouldContinue) {
        this.isDownloading.value = false;
        return;
      }

      const projectId = Store.project.id.value!;
      const projectMetadata = Store.project.metadata.value!;

      const newName = `${projectId}-${Date.now()}`;
      const data = await initApi().copyProjectDoc({ projectId, documentCopyRequest: { newName } });

      if (!data?.storageName) {
        this.props.showActionNotification(
          "error",
          "Unable to add to portfolio. Contact us here",
          undefined,
          COMMUNITY_LINK_WITH_ERROR_UTM,
        );
        return;
      }

      const doc: PortfolioItemProject = {
        type: "PROJECT",
        title: projectMetadata.title!,
        project: {
          id: projectId,
          storageName: data?.storageName,
        },
      };

      const showError = () => {
        this.props.showActionNotification(
          "error",
          "Unable to add to portfolio. Contact us here",
          12000,
          COMMUNITY_LINK_WITH_ERROR_UTM,
        );
      };

      import("@/wc/pages/portfolio/store").then(({ PortfolioStore }) => {
        const hasReachedProLimit = PortfolioStore.hasReachedProLimit();

        if (hasReachedProLimit) {
          this.props.showActionNotification(
            "error",
            "Portfolio limit reached. Upgrade to Pro for more projects.",
            12000,
            () => {
              this.props.close();
              setTimeout(() => {
                openProModal("share_add_project_limit");
              }, 350);
            },
          );
          this.isDownloading.value = false;
          return;
        }

        PortfolioStore.addDocument(doc)
          .then(document => {
            if (!document) {
              return showError();
            }

            const projectUrl = createPortfolioDocumentationLink(document.id!);
            this.props.showActionNotification("success", "Saved! View project in your portfolio", 24000, projectUrl);
          })
          .catch(showError)
          .finally(() => {
            this.isDownloading.value = false;
          });
      });
    } catch (error) {
      this.props.showActionNotification("error", "Unable to save to portfolio, please try again.");
    }
  }

  @bind
  private async handleCopyURL() {
    try {
      // Go through the reprocess flow but don't block on it
      this.handleReprocess();

      const documentationLinks = createDocumentationLinks(Store.user.getNickname());
      if (!documentationLinks) {
        this.props.showActionNotification("error", "Unable to copy link");
        return;
      }

      const link = documentationLinks.hrefUrl;
      await addToClipboard(link);

      setTimeout(() => {
        this.props.showActionNotification("success", "Link copied!", 8000);
        this.isDownloading.value = false;
      }, 600);
    } catch (error) {
      this.props.showActionNotification("error", "Unable to copy link, please try again.");
      this.isDownloading.value = false;
    }
  }

  @bind
  private trackSocialShareClicked(option: ShareOption) {
    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackSocialShareClicked(this.props.data.projectId, option);
    });
  }

  @bind
  private trackDocumentationViewed() {
    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackDocumentationViewed(this.props.data.projectId);
    });
  }

  @bind
  private handleActionSelection(action: ShareAction) {
    this.selectedSaveProjectOption.value = action;

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackProjectShareOptionClicked(this.props.data.projectId, action);
    });
  }

  @bind
  private async handleFacebookShare() {
    const template = this.getTemplate(ShareOption.FACEBOOK);

    if (!template) {
      return;
    }

    const documentationUrl = generatePrefilledPostUrl(undefined, "facebook");
    await addToClipboard(template);

    window.open(documentationUrl, "_blank");

    this.props.showActionNotification("success", "Your Facebook post has been copied to your clipboard", 30000);
    this.trackSocialShareClicked(ShareOption.FACEBOOK);
  }

  @bind
  private async copyPostTemplate(option: ShareOption) {
    const metadata = Store.project.metadata.value;

    if (!metadata) {
      return;
    }

    const template = this.getTemplate(option);

    if (isEmpty(template)) {
      this.props.showActionNotification("error", "Unable to copy post template");
      return;
    }

    await addToClipboard(template);
    this.trackSocialShareClicked(option);
  }

  @bind
  getTemplate(option: ShareOption): string {
    const metadata = Store.project.metadata.value;
    const shareTemplate = metadata?.sharetemplate ?? "";

    switch (option) {
      case ShareOption.INSTAGRAM:
        return metadata?.instagramtemplate ?? shareTemplate;
      case ShareOption.TIKTOK:
        return metadata?.shortformtemplate ?? shareTemplate;
      case ShareOption.LINKEDIN:
        return metadata?.linkedintemplate ?? shareTemplate;
      case ShareOption.TWITTER:
        return metadata?.twittertemplate ?? shareTemplate;
      case ShareOption.FACEBOOK:
        return metadata?.facebooktemplate ?? shareTemplate;
      default:
        return shareTemplate;
    }
  }

  @bind
  private handleOnThemeSelect(theme: DocumentationTheme) {
    if (isProTheme(theme) && !Store.user.isPro()) return;

    this.selectedTheme.value = theme;
  }

  @bind
  private handleOnDocumentURLChange(url: string) {
    this.documentUrl.value = url;
  }

  @bind
  private handlePaywall() {
    if (Store.user.isPro()) {
      return;
    }

    closeModal();
    setTimeout(() => openProModal("project_share_url"), 300);
  }

  @bind
  private renderSaveOptions() {
    let content = undefined;

    if (
      this.selectedSaveProjectOption.value === ShareAction.SAVE_AS_PDF ||
      this.selectedSaveProjectOption.value === ShareAction.SAVE_TO_PORTFOLIO ||
      this.selectedSaveProjectOption.value === ShareAction.COPY_URL
    ) {
      content = (
        <div class="space-y-4">
          <div class="space-y-1.5">
            <h4 className="text-sm font-medium text-secondary">Theme</h4>

            <ThemeSelector
              onThemeSelect={this.handleOnThemeSelect}
              selected={this.selectedTheme.value}
              isProcessing={this.isDownloading.value}
            />
          </div>

          <div class="space-y-1.5">
            <div className="flex items-center space-x-2">
              <h4 className="text-sm font-medium text-secondary">Add URL (optional)</h4>
              <div onClick={this.handlePaywall}>
                <NWProBadge location="share-add-link" />
              </div>
            </div>
            <div>
              <DocumentURL
                onChange={this.handleOnDocumentURLChange}
                url={this.documentUrl.value}
                error={this.documentUrlError.value}
              />
            </div>
          </div>
        </div>
      );
    }

    if (content) {
      return <div className="animate-fadeInFast">{content}</div>;
    }

    return <div></div>;
  }

  render(props: Props) {
    const options = createSaveProjectOptions(this.handleActionSelection);

    const linkedinTemplate = this.getTemplate(ShareOption.LINKEDIN);
    const twitterTemplate = this.getTemplate(ShareOption.TWITTER);
    const isDownloading = this.isDownloading.value;
    const isAsyncAction =
      this.selectedSaveProjectOption.value === ShareAction.SAVE_AS_PDF ||
      this.selectedSaveProjectOption.value === ShareAction.SAVE_TO_PORTFOLIO ||
      this.selectedSaveProjectOption.value === ShareAction.COPY_URL;

    return (
      <div class="content flex flex-col justify-between h-full">
        <div class="flex flex-col">
          <div class="header px-6 pt-6 mb-2 flex items-center justify-between">
            <h3 class="text-lg text-primary font-semibold">Project Sharing</h3>
            <NWCloseButton onClick={props.close} size="md" />
          </div>
          <div class="pb-6 flex-1">
            <div class="flex flex-col space-y-0.5 border-b border-gray-200 pb-4 mb-4">
              <div class="px-6">
                <p class="text-sm text-secondary">
                  Share your project with any of the following options, or choose all! The more you share, the more
                  confident you'll become.
                </p>
              </div>
            </div>

            <div class="my-4 mx-6 space-y-8">
              <div className="space-y-1.5">
                <h4 className="text-sm font-medium text-secondary">Generate social post</h4>
                <div class="grid grid-cols-4 gap-2 relative">
                  <NWButton
                    type="secondary"
                    size="full"
                    onClick={() => {
                      const url = generatePrefilledPostUrl(linkedinTemplate, "linkedin");
                      this.trackSocialShareClicked(ShareOption.LINKEDIN);
                      window.open(url, "_blank");
                    }}
                    className="inline-block"
                  >
                    <NWIcon name="linkedin" width={24} height={24} />
                  </NWButton>

                  <NWButton
                    type="secondary"
                    size="full"
                    className="inline-block"
                    onClick={() => {
                      const url = generatePrefilledPostUrl(twitterTemplate, "twitter");
                      this.trackSocialShareClicked(ShareOption.TWITTER);
                      window.open(url, "_blank");
                    }}
                  >
                    <NWIcon name="twitter" width={24} height={24} />
                  </NWButton>

                  <SocialTooltip heading="Copy post template">
                    <NWButton type="secondary" size="full" onClick={this.handleFacebookShare}>
                      <NWIcon name="facebook" width={24} height={24} />
                    </NWButton>
                  </SocialTooltip>

                  <SocialTooltip heading="Post template copied" trigger="click">
                    <NWButton type="secondary" size="full" onClick={() => this.copyPostTemplate(ShareOption.INSTAGRAM)}>
                      <NWIcon name="instagram" width={24} height={24} />
                    </NWButton>
                  </SocialTooltip>
                </div>
              </div>

              <div className="space-y-1.5">
                <h4 className="text-sm font-medium text-secondary">Save project</h4>
                <div class="w-full">
                  <NWDropdown
                    items={options}
                    width="full"
                    maxHeight="h-36"
                    withChevron
                    fullWidthMenu
                    position="absolute"
                    onItemSelect={this.handleActionSelection}
                  >
                    <div className="flex items-center justify-start">
                      <span class="text-base text-secondary flex items-center">
                        {getIcon(this.selectedSaveProjectOption.value) && (
                          <div class="mr-2">
                            <NWIcon name={getIcon(this.selectedSaveProjectOption.value)!} />
                          </div>
                        )}
                        {getDisplayText(this.selectedSaveProjectOption.value)}
                        {isProOption(this.selectedSaveProjectOption.value) && (
                          <div class="ml-2">
                            <NWProBadge
                              location="share-action-selected"
                              analyticsData={{ action: this.selectedSaveProjectOption.value }}
                            />
                          </div>
                        )}
                      </span>
                    </div>
                  </NWDropdown>
                </div>
              </div>

              {this.renderSaveOptions()}
            </div>
          </div>
        </div>

        <div class="footer border-t flex flex-col p-6 space-y-3">
          <NWButton
            type="primary"
            size="full"
            onClick={this.handleOnSave}
            loading={isDownloading}
            disabled={isAsyncAction && !this.selectedTheme.value}
          >
            {this.selectedSaveProjectOption.value === ShareAction.COPY_URL ? "Copy link" : "Save documentation"}
          </NWButton>
          <NWButton type="secondary" size="full" onClick={props.onBack} icon="arrow-left">
            Back
          </NWButton>
        </div>
      </div>
    );
  }
}
